import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Target, Bug, TrendingUp, Zap } from "lucide-react";

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then(({data}) => setD(data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  const first = (d.user.name || d.user.email).split(" ")[0].split("@")[0];
  const next = d.recommended_lessons[0];
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="rle-eyebrow">Your dashboard</span>
          <h1 className="mt-1 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">Hi, {first} 👋</h1>
          <p className="text-slate-600 mt-1">
            {d.user.country ? `${d.user.country} · ${d.user.english_variant} · ${(d.user.level || "beginner").replace("_"," ")}` : "Complete onboarding to personalise your plan."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {next && (
            <Link to={`/learn/${next.id}`}><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="continue-learning-btn">Continue Learning <ArrowRight size={16} className="ml-1"/></Button></Link>
          )}
          <Link to="/missions"><Button variant="outline" className="rounded-full" data-testid="practise-situation-btn">Practise a Real Situation</Button></Link>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={BookOpen} label="Lessons" value={`${d.stats.lessons_completed}/${d.stats.lessons_total}`} testId="stat-lessons"/>
        <StatCard icon={Target} label="Missions" value={`${d.stats.scenarios_completed}/${d.stats.scenarios_total}`} testId="stat-missions"/>
        <StatCard icon={Bug} label="Mistakes" value={d.stats.mistakes_total} testId="stat-mistakes"/>
        <StatCard icon={TrendingUp} label="Plan" value={d.user.plan} testId="stat-plan"/>
      </div>

      <section>
        <SectionTitle title="Today's practice" subtitle="Continue where you left off." />
        <div className="grid md:grid-cols-3 gap-4">
          {d.recommended_lessons.length ? d.recommended_lessons.map((l) => (
            <Link key={l.id} to={`/learn/${l.id}`} className="rle-card p-5 block" data-testid={`rec-lesson-${l.id}`}>
              <div className="rle-eyebrow">{l.topic}</div>
              <h3 className="mt-2 font-display font-semibold text-slate-900">{l.title}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{l.summary}</p>
              <div className="mt-4 text-sm text-emerald-700 font-medium">Start lesson →</div>
            </Link>
          )) : <EmptyCard text="No lessons available yet for your country. Try changing your country in Profile."/>}
        </div>
      </section>

      <section>
        <SectionTitle title="Real-Life Missions" subtitle="Practise conversations you actually face." />
        <div className="grid md:grid-cols-3 gap-4">
          {d.recommended_scenarios.length ? d.recommended_scenarios.map((s) => (
            <Link key={s.id} to={`/missions/${s.id}`} className="rle-card p-5 block" data-testid={`rec-mission-${s.id}`}>
              <div className="rle-eyebrow">{s.goal}</div>
              <h3 className="mt-2 font-display font-semibold text-slate-900">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{s.context}</p>
              <div className="mt-4 text-sm text-emerald-700 font-medium">Start mission →</div>
            </Link>
          )) : <EmptyCard text="No missions available yet."/>}
        </div>
      </section>

      <section>
        <SectionTitle title="Today's 2-minute drill" subtitle="Turn your recent mistakes into quick fix-it practice."/>
        <Link to="/drills" className="rounded-2xl p-5 flex items-center justify-between gap-4 bg-emerald-700 text-white hover:bg-emerald-800 transition-colors shadow-sm" data-testid="dashboard-drill-link">
          <div className="flex items-center gap-3"><Zap size={20}/><div><div className="font-display font-semibold">Start today's drill</div><div className="text-sm text-emerald-100">~5 quick items from your Mistake Bank</div></div></div>
          <ArrowRight size={18}/>
        </Link>
      </section>

      <section>
        <SectionTitle title="Mistakes to review" subtitle={d.entitlements.mistake_bank === "preview" ? "Preview only — upgrade to review your full mistake bank." : "Fix the recurring ones first."}/>
        <Link to="/mistakes" className="rle-card p-5 block text-emerald-700 font-medium" data-testid="dashboard-mistakes-link">Open Mistake Bank →</Link>
      </section>
    </div>
  );
}
const StatCard = ({icon: Icon, label, value, testId}) => (
  <div className="rle-card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><Icon size={14}/>{label}</div>
    <div className="mt-2 font-display font-bold text-2xl text-slate-900 capitalize">{value}</div>
  </div>
);
const SectionTitle = ({title, subtitle}) => (
  <div className="mb-4"><h2 className="font-display font-bold text-xl text-slate-900">{title}</h2>{subtitle && <p className="text-sm text-slate-600">{subtitle}</p>}</div>
);
const EmptyCard = ({text}) => <div className="rle-card p-6 text-slate-500 text-sm">{text}</div>;
